import type { Look, TimePreset } from '@better-lights/core';
import { create } from 'zustand';

export type InspectorTab = 'lines' | 'lights' | 'ai';
export type DepthStatus = 'idle' | 'loading-model' | 'running' | 'ready' | 'unavailable' | 'error' | 'off';

/**
 * Tier 2 — editor UI state: not undoable, not saved with the project. Per-frame gesture
 * state (transform, hover, drags) is tier 3 and lives in plain refs (editor/canvas/live.ts).
 */
export interface UiState {
  tab: InspectorTab;
  expanded: boolean;
  compare: boolean;
  guidesHidden: boolean;
  exportOpen: boolean;
  helpOpen: boolean;
  quickColorsOpen: boolean;
  /** AI render panel selections, kept for the app session. */
  aiTimes: TimePreset[];
  /** AI looks to render (docs/spec/10); null = the project's color with permanent lights. */
  aiLooks: Look[] | null;
  depth: { status: DepthStatus; detail?: string; progress?: number };
  /** Apple Pencil / stylus seen (auto mode), remembered for the app run (fixes 07 §B29). */
  pencilSeen: boolean;
  /** Export sheet: add the legend strip to Lines exports (resets when the editor mounts). */
  exportLegend: boolean;
  /** Export sheet: include lengths & prices in the legend (resets when the editor mounts). */
  exportShowLengths: boolean;
  /** Paywall dialog: out of credits, or a feature the plan lacks (pre-selects `plan`). */
  paywall: null | { reason: 'credits' | 'feature'; feature?: string; plan?: string; orgId?: string };
}

export const useUi = create<UiState>(() => ({
  tab: 'lines',
  expanded: false,
  compare: false,
  guidesHidden: false,
  exportOpen: false,
  helpOpen: false,
  quickColorsOpen: false,
  aiTimes: ['night'],
  aiLooks: null,
  depth: { status: 'idle' },
  pencilSeen: false,
  exportLegend: true,
  exportShowLengths: true,
  paywall: null,
}));

export const setUi = (p: Partial<UiState>) => useUi.setState(p);

/** Select a tab and expand the inspector (HUD shortcuts). */
export function openTab(tab: InspectorTab): void {
  useUi.setState({ tab, expanded: true });
}
